import { useId, useRef, useState, type ReactNode } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormLayout, FormSection } from '@nucleus-commerce/admin-kit/forms';
import { useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import type { SizeSetSystem } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { equivalentColumns } from '../api/size-set-mappers';
import { useSizeGuidePages } from '../hooks/use-size-guide-pages';
import {
  EQUIVALENT_SYSTEMS,
  SIZE_SYSTEMS,
  sizeSetFormSchema,
  type SizeSetFormInput,
  type SizeSetFormValues,
} from '../schemas/size-set-form-schema';

import { SizeSetRangeDialog } from './size-set-range-dialog';
import { SizeSetSizesSection } from './size-set-sizes-section';

export const SIZE_SET_FORM_ID = 'size-set-form';

export interface SizeSetFormProps {
  mode: 'create' | 'edit';
  defaultValues: SizeSetFormInput;
  onSubmit: (values: SizeSetFormValues) => Promise<unknown>;
  isSubmitting: boolean;
  /** Staff without `attributes.write` see the set but cannot change it. */
  readOnly: boolean;
  dangerZone?: ReactNode;
}

const keyFrom = (name: string) =>
  name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^[^a-z]+|_+$/g, '')
    .slice(0, 64);

/**
 * The size set editor: details and the sizes grid in the main column, Save and the danger zone
 * in the rail. Registered with the save bar; the range dialog sits outside the <form> so its own
 * submit never reaches this one.
 */
export function SizeSetForm({
  mode,
  defaultValues,
  onSubmit,
  isSubmitting,
  readOnly,
  dangerZone,
}: SizeSetFormProps) {
  const { t } = useTranslation(['sizeSets', 'common']);
  const message = useErrorMessage();
  const form = useForm<SizeSetFormInput, unknown, SizeSetFormValues>({
    resolver: useI18nResolver(sizeSetFormSchema),
    defaultValues,
  });
  const sizes = useFieldArray({ control: form.control, name: 'values' });
  const applyFieldErrors = useApplyFieldErrors(form.setError);
  const [columns, setColumns] = useState(() => equivalentColumns(defaultValues.values));
  const [rangeOpen, setRangeOpen] = useState(false);
  // The key follows the name until it is edited by hand; a re-render must not stop that.
  const keyTouched = useRef(false);
  const { isDirty } = form.formState;
  const guides = useSizeGuidePages();
  const guideListId = useId();

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await onSubmit(values);
      form.reset(values);
    } catch (error) {
      if (!applyFieldErrors(error)) {
        form.setError('root.server', { type: 'server', message: message(error) });
      }
    }
  });

  const discard = () => {
    form.reset();
    keyTouched.current = false;
    setColumns(equivalentColumns(defaultValues.values));
  };

  useSaveBar({
    form,
    onSave: submit,
    onDiscard: discard,
    saving: isSubmitting,
    disabled: readOnly,
  });

  const removeColumn = (system: SizeSetSystem) => {
    setColumns((current) => current.filter((c) => c !== system));
    form.getValues('values').forEach((row, index) => {
      if (row.equivalents[system]) {
        form.setValue(`values.${index}.equivalents.${system}`, '', { shouldDirty: true });
      }
    });
  };

  const rootError = form.formState.errors.root?.server;

  return (
    <Form {...form}>
      <FormLayout
        aside={
          <>
            <FormSection
              title={t('sizeSets:save.title')}
              description={t('sizeSets:save.description')}
            >
              {readOnly ? (
                <p className="text-muted-foreground text-sm">{t('sizeSets:save.readOnly')}</p>
              ) : (
                <div className="flex items-center gap-2">
                  <Button
                    type="submit"
                    form={SIZE_SET_FORM_ID}
                    disabled={isSubmitting || (mode === 'edit' && !isDirty)}
                    className="flex-1"
                  >
                    {isSubmitting
                      ? t('common:actions.saving')
                      : mode === 'create'
                        ? t('sizeSets:save.create')
                        : t('common:actions.save')}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={isSubmitting || !isDirty}
                    onClick={discard}
                  >
                    {t('sizeSets:save.discard')}
                  </Button>
                </div>
              )}
            </FormSection>
            {dangerZone}
          </>
        }
      >
        <form id={SIZE_SET_FORM_ID} onSubmit={submit} noValidate>
          <fieldset disabled={readOnly} className="flex min-w-0 flex-col gap-6">
            {rootError?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertTitle>{t('sizeSets:saveFailed')}</AlertTitle>
                <AlertDescription>{rootError.message}</AlertDescription>
              </Alert>
            ) : null}
            <FormSection
              title={t('sizeSets:details.title')}
              description={t('sizeSets:details.description')}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('sizeSets:fields.name')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          autoFocus={mode === 'create'}
                          onChange={(e) => {
                            field.onChange(e);
                            if (mode === 'create' && !keyTouched.current) {
                              form.setValue('key', keyFrom(e.target.value));
                            }
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="key"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('sizeSets:fields.key')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          disabled={mode === 'edit'}
                          className="font-mono text-[13px]"
                          onChange={(e) => {
                            keyTouched.current = true;
                            field.onChange(e);
                          }}
                        />
                      </FormControl>
                      <FormDescription>
                        {mode === 'edit'
                          ? t('sizeSets:fields.keyFixed')
                          : t('sizeSets:fields.keyHint')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="system"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('sizeSets:fields.system')}</FormLabel>
                      <Select
                        value={field.value}
                        disabled={readOnly}
                        onValueChange={(value) => {
                          const picked = SIZE_SYSTEMS.find((s) => s === value);
                          if (picked) field.onChange(picked);
                        }}
                      >
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {SIZE_SYSTEMS.map((system) => (
                            <SelectItem key={system} value={system}>
                              {t(`sizeSets:system.${system}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>{t('sizeSets:fields.systemHint')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="unit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('sizeSets:fields.unit')}</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="mm" className="sm:max-w-40" />
                      </FormControl>
                      <FormDescription>{t('sizeSets:fields.unitHint')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="sizeGuideHandle"
                  render={({ field }) => (
                    <FormItem className="sm:col-span-2">
                      <FormLabel>{t('sizeSets:fields.sizeGuide')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          list={guideListId}
                          maxLength={120}
                          className="font-mono text-[13px]"
                          placeholder="ring-size-guide"
                        />
                      </FormControl>
                      <datalist id={guideListId}>
                        {guides.map((guide) => (
                          <option key={guide.handle} value={guide.handle}>
                            {guide.label}
                          </option>
                        ))}
                      </datalist>
                      <FormDescription>
                        {guides.length > 0
                          ? t('sizeSets:fields.sizeGuideHint')
                          : t('sizeSets:fields.sizeGuideNone')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </FormSection>
            <SizeSetSizesSection
              sizes={sizes}
              columns={columns}
              readOnly={readOnly}
              onAddColumn={(system) =>
                setColumns((current) =>
                  EQUIVALENT_SYSTEMS.filter((s) => s === system || current.includes(s)),
                )
              }
              onRemoveColumn={removeColumn}
              onAddRange={() => setRangeOpen(true)}
            />
          </fieldset>
        </form>
      </FormLayout>
      <SizeSetRangeDialog
        open={rangeOpen}
        onOpenChange={setRangeOpen}
        existing={rangeOpen ? form.getValues('values').map((row) => row.value.trim()) : []}
        onAdd={(rows) => sizes.append(rows)}
      />
    </Form>
  );
}
